import { apiGet, apiPost } from '@/lib/api';
import { toast } from 'sonner';

export type MindWiseTestScenario = 
  | "happy_path" 
  | "empty_state" 
  | "duplicate_409" 
  | "network_error" 
  | "high_risk";

export interface MindWiseSyncResponse {
  status: string;
  message?: string;
  already_submitted?: boolean;
  sync_results?: {
    sessionId?: string;
    patientId?: string;
    organizationId?: string;
    performedAt?: string;
    phq9?: {
      http_status: number;
      data: {
        total?: number;
        severity?: string;
        selfHarmRisk?: boolean;
        recordedAt?: string;
        loincCode?: string;
        status?: string;
      };
      answers_sent: number[];
    };
    gad7?: {
      http_status: number;
      data: {
        total?: number;
        severity?: string;
        recordedAt?: string;
        loincCode?: string;
        status?: string;
      };
      answers_sent: number[];
    };
    note?: {
      id?: string;
      summary?: string;
      noteType?: string;
      performedBy?: string;
      outreachChannel?: string;
      recordedAt?: string;
    };
  };
}

export interface MindWiseSyncLog {
  id: number;
  patient_id: string;
  mindwise_patient_id: string;
  session_id: string;
  instrument: string;
  total_score: number | null;
  severity: string | null;
  self_harm_risk: boolean;
  status: string;
  mindwise_http_status: number;
  synced_at: string;
  reference_id?: string;
}

export interface MindWiseRosterPatient {
  id: string;
  mindwise_uuid: string;
  first_name: string;
  last_name: string;
  dob: string;
  mobile_number: string;
  next_visit: string;
  screening_status: "COMPLETED" | "SCHEDULED" | "AWAITING_RESPONSE";
  last_ingested: string;
  phq9_score?: number | null;
  gad7_score?: number | null;
}

export interface MindWiseWeeklyStats {
  ingestedThisWeek: boolean;
  cycleName: string;
  ingestedCount: number;
  completedScreenings: number;
  pendingScreenings: number;
  lastIngestedAt: string;
  nextScheduledAt: string;
  patients: MindWiseRosterPatient[];
}

// ── Mock Configuration & State ──────────────────────────────────────────────

let isMockMode = true;
let currentScenario: MindWiseTestScenario = "happy_path";
let mockRosterPulledOnce = false;

const INITIAL_MOCK_SYNC_LOGS: MindWiseSyncLog[] = [
  {
    id: 101,
    patient_id: "demo-pt-1",
    mindwise_patient_id: "MW-PT-9042",
    session_id: "sess_mw_7b8192f0c8e2",
    instrument: "PHQ-9",
    total_score: 12,
    severity: "Moderate Depression",
    self_harm_risk: false,
    status: "SYNCED",
    mindwise_http_status: 201,
    synced_at: new Date(Date.now() - 3600000 * 24 * 7).toISOString(),
    reference_id: "mh-phq9-8831a"
  },
  {
    id: 102,
    patient_id: "demo-pt-1",
    mindwise_patient_id: "MW-PT-9042",
    session_id: "sess_mw_7b8192f0c8e2",
    instrument: "GAD-7",
    total_score: 10,
    severity: "Moderate Anxiety",
    self_harm_risk: false,
    status: "SYNCED",
    mindwise_http_status: 201,
    synced_at: new Date(Date.now() - 3600000 * 24 * 7).toISOString(),
    reference_id: "mh-gad7-8831b"
  },
  {
    id: 99,
    patient_id: "demo-pt-1",
    mindwise_patient_id: "MW-PT-9042",
    session_id: "sess_mw_5a1098e1f10a",
    instrument: "PHQ-9",
    total_score: 16,
    severity: "Moderately Severe",
    self_harm_risk: false,
    status: "SYNCED",
    mindwise_http_status: 201,
    synced_at: new Date(Date.now() - 3600000 * 24 * 21).toISOString(),
    reference_id: "mh-phq9-7719c"
  }
];

let localMockLogs: MindWiseSyncLog[] = [...INITIAL_MOCK_SYNC_LOGS];

export const MOCK_WEEKLY_ROSTER: MindWiseRosterPatient[] = [
  {
    id: "demo-pt-1",
    mindwise_uuid: "MW-PT-9042",
    first_name: "Sarah",
    last_name: "Connor",
    dob: "1984-05-12",
    mobile_number: "+1 (555) 234-5678",
    next_visit: "2026-10-05T10:00:00Z",
    screening_status: "COMPLETED",
    last_ingested: "2026-09-28T08:00:00Z",
    phq9_score: 12,
    gad7_score: 10
  },
  {
    id: "demo-pt-2",
    mindwise_uuid: "MW-PT-9043",
    first_name: "John",
    last_name: "Doe",
    dob: "1990-11-20",
    mobile_number: "+1 (555) 345-6789",
    next_visit: "2026-10-06T14:30:00Z",
    screening_status: "SCHEDULED",
    last_ingested: "2026-09-28T08:00:00Z"
  },
  {
    id: "demo-pt-3",
    mindwise_uuid: "MW-PT-9044",
    first_name: "Elena",
    last_name: "Rostova",
    dob: "1978-03-15",
    mobile_number: "+1 (555) 456-7890",
    next_visit: "2026-10-07T09:15:00Z",
    screening_status: "AWAITING_RESPONSE",
    last_ingested: "2026-09-28T08:00:00Z"
  },
  {
    id: "demo-pt-4",
    mindwise_uuid: "MW-PT-9045",
    first_name: "Marcus",
    last_name: "Vance",
    dob: "1995-08-24",
    mobile_number: "+1 (555) 567-8901",
    next_visit: "2026-10-08T11:00:00Z",
    screening_status: "SCHEDULED",
    last_ingested: "2026-09-28T08:00:00Z"
  },
  {
    id: "demo-pt-5",
    mindwise_uuid: "MW-PT-9046",
    first_name: "Rachel",
    last_name: "Green",
    dob: "1988-12-05",
    mobile_number: "+1 (555) 678-9012",
    next_visit: "2026-10-09T16:00:00Z",
    screening_status: "COMPLETED",
    last_ingested: "2026-09-28T08:00:00Z",
    phq9_score: 8,
    gad7_score: 6
  }
];

function buildMockResponse(
  patientId: string, 
  scenario: MindWiseTestScenario
): MindWiseSyncResponse {
  const sessionId = `sess_mw_${Math.random().toString(36).substring(2, 8)}${Math.random().toString(36).substring(2, 6)}`;
  const mwPatientId = `MW-PT-${Math.floor(1000 + Math.random() * 9000)}`;
  const now = new Date().toISOString();

  if (scenario === "duplicate_409") {
    return {
      status: "success",
      already_submitted: true,
      message: "Idempotent Sync: MindWise returned 409 Conflict. Original assessment results safely retained.",
      sync_results: {
        sessionId: "sess_mw_prior_session_8819",
        patientId: mwPatientId,
        organizationId: "org_mindwise_dev_01",
        performedAt: new Date(Date.now() - 3600000).toISOString(),
        note: {
          id: "note_mw_duplicate_01",
          summary: "Pre-existing outreach session retained. Duplicate submission averted safely.",
          noteType: "outreach_assessment",
          performedBy: "Member",
          outreachChannel: "SMS",
          recordedAt: new Date(Date.now() - 3600000).toISOString()
        },
        phq9: {
          http_status: 409,
          data: {
            total: 12,
            severity: "Moderate Depression",
            selfHarmRisk: false,
            loincCode: "44261-6",
            status: "Already Submitted",
            recordedAt: new Date(Date.now() - 3600000).toISOString()
          },
          answers_sent: [2, 2, 1, 2, 1, 1, 2, 1, 0]
        },
        gad7: {
          http_status: 409,
          data: {
            total: 10,
            severity: "Moderate Anxiety",
            loincCode: "70274-6",
            status: "Already Submitted",
            recordedAt: new Date(Date.now() - 3600000).toISOString()
          },
          answers_sent: [2, 1, 2, 1, 1, 1, 2]
        }
      }
    };
  }

  const isHighRisk = scenario === "high_risk";
  const phq9Answers = isHighRisk 
    ? [3, 2, 2, 3, 2, 2, 3, 2, 2] // High score with Item 9 = 2
    : [2, 2, 1, 2, 1, 1, 2, 1, 0]; // Moderate score, Item 9 = 0
  const phq9Total = phq9Answers.reduce((a, b) => a + b, 0);

  const gad7Answers = isHighRisk 
    ? [3, 2, 3, 2, 3, 2, 3] 
    : [2, 1, 2, 1, 1, 1, 2];
  const gad7Total = gad7Answers.reduce((a, b) => a + b, 0);

  const response: MindWiseSyncResponse = {
    status: "success",
    message: isHighRisk 
      ? "MindWise Sync Complete (URGENT: Suicide Risk Item 9 Flagged Positive)"
      : "Successfully synchronized clinical encounter note and assessments to MindWise EHR",
    sync_results: {
      sessionId,
      patientId: mwPatientId,
      organizationId: "org_mindwise_dev_01",
      performedAt: now,
      note: {
        id: `note_mw_${Math.floor(10000 + Math.random() * 90000)}`,
        summary: `Completed behavioral health screening for patient via SMS/Mobile. Encounter filed under MindWise EMR.${isHighRisk ? " [URGENT CARE ALERT: Item 9 positive]" : ""}`,
        noteType: "outreach_assessment",
        performedBy: "Member",
        outreachChannel: "SMS",
        recordedAt: now
      },
      phq9: {
        http_status: 201,
        data: {
          total: phq9Total,
          severity: isHighRisk ? "Severe Depression" : "Moderate Depression",
          selfHarmRisk: isHighRisk,
          loincCode: "44261-6",
          status: "In Review",
          recordedAt: now
        },
        answers_sent: phq9Answers
      },
      gad7: {
        http_status: 201,
        data: {
          total: gad7Total,
          severity: isHighRisk ? "Severe Anxiety" : "Moderate Anxiety",
          loincCode: "70274-6",
          status: "In Review",
          recordedAt: now
        },
        answers_sent: gad7Answers
      }
    }
  };

  // Prepend into local audit history logs
  localMockLogs.unshift(
    {
      id: localMockLogs.length + 101,
      patient_id: patientId,
      mindwise_patient_id: mwPatientId,
      session_id: sessionId,
      instrument: "PHQ-9",
      total_score: phq9Total,
      severity: isHighRisk ? "Severe Depression" : "Moderate Depression",
      self_harm_risk: isHighRisk,
      status: "SYNCED",
      mindwise_http_status: 201,
      synced_at: now,
      reference_id: `mh-phq9-${Math.random().toString(36).substring(2, 7)}`
    },
    {
      id: localMockLogs.length + 102,
      patient_id: patientId,
      mindwise_patient_id: mwPatientId,
      session_id: sessionId,
      instrument: "GAD-7",
      total_score: gad7Total,
      severity: isHighRisk ? "Severe Anxiety" : "Moderate Anxiety",
      self_harm_risk: false,
      status: "SYNCED",
      mindwise_http_status: 201,
      synced_at: now,
      reference_id: `mh-gad7-${Math.random().toString(36).substring(2, 7)}`
    }
  );

  return response;
}

// ── MindWise Service API ────────────────────────────────────────────────────

const MindWiseService = {
  isMockMode(): boolean {
    return isMockMode;
  },

  setMockMode(enabled: boolean) {
    isMockMode = enabled;
    console.info(`[MindWiseService] Mock Mode set to: ${enabled}`);
  },

  getTestScenario(): MindWiseTestScenario {
    return currentScenario;
  },

  setTestScenario(scenario: MindWiseTestScenario) {
    currentScenario = scenario;
    console.info(`[MindWiseService] Active Test Scenario set to: ${scenario}`);
  },

  /**
   * Main sync method called by UI button
   */
  async syncPatientAssessments(patientId: string): Promise<MindWiseSyncResponse> {
    const loadingToast = toast.loading("Syncing assessments to MindWise EMR...");

    if (isMockMode) {
      // Simulate realistic latency
      await new Promise((resolve) => setTimeout(resolve, 950));
      toast.dismiss(loadingToast);

      // TC-MW-03: Empty State Handling
      if (currentScenario === "empty_state") {
        const errorMsg = "No PHQ-9 or GAD-7 responses found for this patient";
        toast.warning(errorMsg, {
          description: "Patient has not completed any questionnaires yet. Assessment sync halted."
        });
        const err = new Error(errorMsg);
        (err as any).status = 400;
        throw err;
      }

      // TC-MW-05: Backend Error / Server Offline Resilience
      if (currentScenario === "network_error") {
        const errorMsg = "MindWise Sync Failed — Network Error (Mock)";
        toast.error(errorMsg, {
          description: "Unable to reach MindWise server. Check connection and retry."
        });
        const err = new Error(errorMsg);
        (err as any).status = 502;
        throw err;
      }

      // TC-MW-04: Idempotency & 409 duplicate handling
      if (currentScenario === "duplicate_409") {
        const mockResult = buildMockResponse(patientId, "duplicate_409");
        toast.info("Duplicate Assessment Sync Handled (409)", {
          description: "MindWise EMR already holds this session's score. Original record preserved."
        });
        return mockResult;
      }

      // TC-MW-02 (Happy Path) or High Risk (Self Harm trigger)
      const mockResult = buildMockResponse(patientId, currentScenario);
      if (currentScenario === "high_risk") {
        toast.error("⚠️ URGENT: Item 9 Suicide Risk Flagged!", {
          description: "MindWise evaluated selfHarmRisk: true. Immediate clinical review required."
        });
      } else {
        toast.success("MindWise Sync Complete (Mock Data)", {
          description: `Outreach note & assessments filed (Session: ${mockResult.sync_results?.sessionId})`
        });
      }
      return mockResult;
    }

    // Live Backend Call
    try {
      const response = await apiPost<MindWiseSyncResponse>(`/mindwise/v2/sync-patient/${patientId}`, {});
      toast.dismiss(loadingToast);
      toast.success("Successfully synced assessments to MindWise!");
      return response;
    } catch (error: any) {
      toast.dismiss(loadingToast);
      const msg = error.message || "Failed to sync to MindWise";
      toast.error(msg);
      throw error;
    }
  },

  /**
   * Pulls sync history logs for a patient
   */
  async getSyncLogs(patientId: string): Promise<MindWiseSyncLog[]> {
    if (isMockMode) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      return [...localMockLogs];
    }

    try {
      const res = await apiGet<{ status: string; logs: MindWiseSyncLog[] }>(`/mindwise/v2/sync-logs/${patientId}`);
      return res?.logs || [];
    } catch (error) {
      console.warn("Failed to fetch MindWise sync logs:", error);
      return [];
    }
  },

  /**
   * Pulls latest patients and upcoming visits from MindWise roster API
   */
  async pullPatientsFromMindWise(): Promise<any> {
    const loadingToast = toast.loading("Pulling roster from MindWise...");

    if (isMockMode) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      toast.dismiss(loadingToast);

      if (mockRosterPulledOnce || currentScenario === "empty_state") {
        toast.info("No new patient data to be retrieved — roster is up to date");
        return {
          status: "success",
          imported_count: 0,
          new_patients_count: 0,
          message: "No new patient data to be retrieved",
          patients: currentScenario === "empty_state" ? [] : [...MOCK_WEEKLY_ROSTER]
        };
      }

      mockRosterPulledOnce = true;
      toast.success("Retrieved 5 mock patients from MindWise roster");
      return {
        status: "success",
        imported_count: 5,
        new_patients_count: 5,
        patients: [
          { mindwise_uuid: "MW-PT-9042", first_name: "Sarah", last_name: "Connor", next_visit: "2026-10-05T10:00:00Z" },
          { mindwise_uuid: "MW-PT-9043", first_name: "John", last_name: "Doe", next_visit: "2026-10-06T14:30:00Z" },
          { mindwise_uuid: "MW-PT-9044", first_name: "Elena", last_name: "Rostova", next_visit: "2026-10-07T09:15:00Z" },
          { mindwise_uuid: "MW-PT-9045", first_name: "Marcus", last_name: "Vance", next_visit: "2026-10-08T11:00:00Z" },
          { mindwise_uuid: "MW-PT-9046", first_name: "Rachel", last_name: "Green", next_visit: "2026-10-09T16:00:00Z" },
        ]
      };
    }

    try {
      const res: any = await apiPost('/mindwise/pull-patients', {});
      toast.dismiss(loadingToast);

      const newPatients = res?.patients_inserted ?? res?.imported_count ?? 0;
      const updatedPatients = res?.patients_updated ?? 0;

      if (res?.status === "warning" || (newPatients === 0 && updatedPatients === 0)) {
        toast.info("No new patient data to be retrieved — roster is up to date");
      } else {
        toast.success(`MindWise roster pulled: ${newPatients} new, ${updatedPatients} updated`);
      }
      return res;
    } catch (error: any) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to pull MindWise roster");
      throw error;
    }
  },

  /**
   * Resets the mock roster pull state (useful for testing)
   */
  resetMockRosterState() {
    mockRosterPulledOnce = false;
  },

  /**
   * Retrieves summary statistics for MindWise weekly data ingestion
   */
  getWeeklyIngestionStats(): MindWiseWeeklyStats {
    const completed = MOCK_WEEKLY_ROSTER.filter(p => p.screening_status === "COMPLETED").length;
    return {
      ingestedThisWeek: true,
      cycleName: "Current Week (Cycle 40)",
      ingestedCount: MOCK_WEEKLY_ROSTER.length,
      completedScreenings: completed,
      pendingScreenings: MOCK_WEEKLY_ROSTER.length - completed,
      lastIngestedAt: "2026-09-28T08:00:00Z",
      nextScheduledAt: "2026-10-05T08:00:00Z",
      patients: [...MOCK_WEEKLY_ROSTER]
    };
  },

  /**
   * Returns list of patients ingested in this week's cycle
   */
  getWeeklyIngestedRoster(): MindWiseRosterPatient[] {
    return [...MOCK_WEEKLY_ROSTER];
  },

  // ── Convenient QA Test Scenario Triggers ──────────────────────────────────
  async testHappyPath(patientId: string = "demo-pt") {
    currentScenario = "happy_path";
    return this.syncPatientAssessments(patientId);
  },

  async testEmptyState(patientId: string = "demo-pt") {
    currentScenario = "empty_state";
    return this.syncPatientAssessments(patientId);
  },

  async testDuplicate409(patientId: string = "demo-pt") {
    currentScenario = "duplicate_409";
    return this.syncPatientAssessments(patientId);
  },

  async testNetworkError(patientId: string = "demo-pt") {
    currentScenario = "network_error";
    return this.syncPatientAssessments(patientId);
  },

  async testHighRisk(patientId: string = "demo-pt") {
    currentScenario = "high_risk";
    return this.syncPatientAssessments(patientId);
  }
};

// Expose on window for DevTools testing (TC-MW-06)
if (typeof window !== "undefined") {
  (window as any).MindWiseService = MindWiseService;
}

export default MindWiseService;
